import Downshift, { StateChangeOptions } from 'downshift';
import { Component, ForwardedRef } from 'react';

import { FloatingMenu, FloatingMenuProps } from '../../forms/FloatingMenu';
import { IconName } from '../../display/Icon';
import {
  TypeaheadInput,
  type Props as TypeaheadInputProps,
} from './TypeaheadInput';

import styles from './Typeahead.module.scss';

type Props<T = any> = {
  /**
   * Browser `autocomplete` form field attribute.
   */
  autoComplete?: string;
  /**
   * Accessibility label that describes this component. This should be supplied
   * if no corresponding `label` element is used.
   */
  'aria-label'?: string;
  /**
   * The text that renders in a `Tooltip` when the input `isDisabled`.
   * Only renders when `isDisabled`.
   */
  disabledText?: string;
  /**
   * Custom filter function to filter list of dropdown items matching inputValue.
   * If not supplied defaults to a simple `includes` filter function (case insensitive).
   */
  filterItems: (
    inputValue: string | null,
    items: Array<T>,
    getLabelForItem?: (item: T) => string,
    showEmptyList?: boolean
  ) => Array<T>;
  /**
   * Callback that will be used to generate the react `key` to be used for each
   * item on the menu list.  Defaults to the index of each item in the array.
   */
  getKeyForItem?: (item: T) => string | number;
  /**
   * Callback that will be used to generate the string label to be rendered for
   * each item on the menu list.
   */
  getLabelForItem: (item: T) => string;
  /**
   * An optional icon name to render on the right side of the input element.
   * Only visible when the X clear selection is not shown.
   */
  iconEndName?: IconName;
  /**
   * HTML `id` attribute for the `input` element. This prop is named `inputId`
   * instead of `id` for consistency with the Downshift API.
   */
  inputId?: string;
  /**
   * For a controlled component, this is the value that will render within the input.
   * Use in conjunction with `selectedItem`.
   */
  inputValue?: string;
  /**
   * Displays an inline loading spinner in the place of the left icon if set to true.
   */
  isBusy?: boolean;
  /**
   * Disables the input and makes it non-interactive.
   */
  isDisabled?: boolean;
  /**
   * Indicates whether the field has an error.
   */
  isErrored?: boolean;
  /**
   * HTML `id` to use for the node.
   */
  id?: string;
  /**
   * The list of items.
   */
  items: Array<T>;
  /**
   * True to keep string value in input when user hasn't selected an item
   * on control blur
   */
  keepInputValueOnBlur?: boolean;
  /**
   * `id` of the label that connects to this select field. This prop is
   * required because Downshift demands it. If we don't provide this prop to
   * Downshift, it will render a bogus, auto-generated aria-labelledby value.
   */
  labelId?: string;
  /**
   * Accessibility label that describes the options available inside the select
   * menu, e.g. "List of items", "List of Countries", etc.
   */
  menuAriaLabel: string;
  /**
   * Callback that gets called when the input loses focus.
   */
  onBlur?: (event: React.SyntheticEvent) => void;
  /**
   * Callback that gets called when the user selects an item and the selected item has changed.
   */
  onChange: (item: T) => void;
  /**
   * Callback that gets called as the user changes the value while typing. It
   * is passed the current value of the `Typeahead` input field.
   */
  onInputValueChange?: (inputValue: string) => void;
  /**
   * Callback that gets called on the keydown event.
   */
  onKeyDown?: (event: React.SyntheticEvent) => void;
  /**
   * Optional prop allowing the menu to open when this element is focused.
   */
  openOnFocus: boolean;
  /**
   * Placeholder to render inside the select toggle when no item is selected.
   */
  placeholder: string;
  /**
   * Optional prop for setting the initially selected item, but still letting `Typeahead` manage
   * the selected item state for you.
   */
  initialSelectedItem?: T | null;
  /** A ref forwarded to the input DOM element */
  inputRef?: ForwardedRef<HTMLInputElement>;
  /**
   * A render prop which receives a menu `item` and the `getLabelForItem`
   * function and returns a React element, which is an icon illustrating the
   * given menu item. The natural size for the icon is 30x30px. If not
   * provided, no icons will be rendered.
   */
  renderIcon?: (
    item: T,
    getLabelForItem: (item: T) => string
  ) => React.ReactElement | null;
  /**
   * A render prop for creating the floating menu.
   */
  renderFloatingMenu: (props: FloatingMenuProps) => React.ReactElement;
  /**
   * An optional function to hold an external reference to Downshift's clearSelection prop
   */
  setClearSelection?: (f: Function) => Function;
  /**
   * For a controlled component, pass in the selected item from items.
   * To prevent the component from becoming uncontrolled, pass `null`
   * instead of `undefined`. Use in conjunction with `inputValue`.
   */
  selectedItem?: T | null;
  /**
   * An optional prop to show an empty list to be passed into callbacks.
   */
  showEmptyList?: boolean;
  /**
   * An optional prop to show the error state when no results are matched to user input.
   */
  showErrorOnNoResults?: boolean;
  /**
   * The rendered size of the input.
   */
  size?: TypeaheadInputProps['size'];
};

const dedupeAndTrimWhitespace = (query: string) =>
  query.replace(/(\s+)/g, ' ').trim();

const defaultFilterItems = <T extends any>(
  inputValue: string | null,
  items: Array<T>,
  getLabelForItem?: (item: T) => string,
  showEmptyList: boolean = false
) => {
  if (showEmptyList || !inputValue) {
    return items;
  }
  const lowerCaseInput = inputValue.toLowerCase();
  return items.filter((item) => {
    // NOTE: In addition to trimming leading and trailing whitespace, the regex
    // below replaces sequences of two or more whitespaces (including tabs/
    // linefeed/carriage-return with a single space -- this should help even when
    // users paste their query into the input.
    return getLabelForItem!(item)
      .toLowerCase()
      .includes(dedupeAndTrimWhitespace(lowerCaseInput));
  });
};

/**
 * <span style="color: #a81227; font-weight: bold;">⚠️ This component is on its way out in favor of [Typeahead β](../?path=/docs/beta-typeahead--docs).</span>
 *
 * An alternative to a `<select>` field. Allows entering a search term to filter the list of items for easier selection.
 *
 * Features:
 * - Filtering of items by typing a search query in the select box
 * - Rendering of custom icons for each menu item
 * - Keyboard support
 * - Accessible
 */
export class Typeahead<T = any> extends Component<Props<T>> {
  static defaultProps = {
    filterItems: defaultFilterItems,
    isDisabled: false,
    openOnFocus: false,
    renderFloatingMenu: (props: FloatingMenuProps) => (
      <FloatingMenu {...props} />
    ),
    showEmptyList: false,
    showErrorOnNoResults: false,
  };

  // Using a custom `itemToString` instead of `props.getLabelForItem` directly to
  // allow the consumer of the component to avoid the complexity of handling
  // null items.
  itemToString = (item?: T) => {
    return item ? this.props.getLabelForItem(item) : '';
  };

  /**
   * Take over some of the state management from Downshift
   * to handle different UX cases.
   */
  stateReducer = (state: any, changes: StateChangeOptions<T>) => {
    // When `keepInputValueOnBlur` is `true`, leaving an input via blur or mouseup
    // should keep the existing `inputValue` (even if it's not in `props.items`).
    // `changes.inputValue` will be an empty string if `inputValue` isn't in `items`,
    // so using `state.inputValue` keeps the value, regardless of its "validity."
    if (
      this.props.keepInputValueOnBlur &&
      (changes.type === Downshift.stateChangeTypes.blurInput ||
        changes.type === Downshift.stateChangeTypes.mouseUp)
    ) {
      return {
        ...changes,
        inputValue: state.inputValue,
      };
    }
    return changes;
  };

  render() {
    const { props } = this;

    return (
      <Downshift
        id={props.id}
        initialSelectedItem={props.selectedItem || props.initialSelectedItem}
        inputId={props.inputId}
        inputValue={props.inputValue}
        itemToString={this.itemToString}
        labelId={props.labelId}
        onChange={props.onChange}
        onInputValueChange={props.onInputValueChange}
        selectedItem={props.selectedItem}
        stateReducer={this.stateReducer}
      >
        {({
          clearSelection,
          getInputProps,
          getItemProps,
          getMenuProps,
          getRootProps,
          highlightedIndex,
          // This does exist, it's just not in the typings.
          // We're using an old version of downshift
          // @ts-ignore
          inputId,
          inputValue,
          isOpen,
          openMenu,
          selectedItem,
        }) => {
          if (props.setClearSelection) {
            props.setClearSelection(clearSelection);
          }

          // Downshift always renders an auto-generated aria-labelledby value
          // which refers to no actual element on the page. If we want to
          // omit a <label> element and instead label this component directly
          // via aria-label, Downshift will continue to render the bogus
          // aria-labelledby. The solution is therefore to force a null
          // aria-labelledby if no label is passed.
          const ariaLabelledBy = props.labelId || null;

          const { innerRef, ...rootProps } = getRootProps(
            {
              // @ts-ignore
              'aria-labelledby': ariaLabelledBy,
              refKey: 'innerRef',
            },
            { suppressRefError: true }
          );

          const filteredItems = props.filterItems(
            inputValue,
            props.items,
            props.getLabelForItem,
            props.showEmptyList || false
          );
          const showMenu = isOpen && filteredItems.length > 0;
          const isErrored =
            (props.showErrorOnNoResults &&
              inputValue &&
              filteredItems.length === 0) ||
            props.isErrored;

          return (
            <div {...rootProps} className={styles.root} ref={innerRef} data-zds>
              <TypeaheadInput
                aria-label={props['aria-label']}
                aria-labelledby={ariaLabelledBy}
                autoComplete={props.autoComplete}
                clearSelection={clearSelection}
                disabledText={props.disabledText}
                getInputProps={getInputProps}
                getLabelForItem={props.getLabelForItem}
                iconEndName={props.iconEndName}
                inputValue={inputValue}
                isBusy={props.isBusy}
                isDisabled={props.isDisabled}
                isErrored={!!isErrored}
                onFocus={props.openOnFocus ? openMenu : undefined}
                onKeyDown={props.onKeyDown}
                placeholder={props.placeholder}
                ref={props.inputRef}
                renderIcon={props.renderIcon}
                selectedItem={selectedItem}
                size={props.size}
                onBlur={props.onBlur}
              />
              {showMenu &&
                props.renderFloatingMenu({
                  'aria-label': props.menuAriaLabel,
                  getItemProps,
                  getKeyForItem: props.getKeyForItem,
                  getLabelForItem: props.getLabelForItem,
                  getMenuProps,
                  highlightedIndex,
                  items: filteredItems,
                  renderIcon: props.renderIcon,
                })}
            </div>
          );
        }}
      </Downshift>
    );
  }
}

export default Typeahead;
